import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const BandContext = createContext(null);

function BandProvider({ bandId }) {
  const { user } = useAuth();
  const [band, setBand] = useState(null);
  const [error, setError] = useState('');
  const [generation, setGeneration] = useState(0);
  const libraryApi = useMemo(() => api.forBand(bandId), [bandId]);
  const refreshBand = useCallback(() => setGeneration((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const data = await api.getBand(bandId);
        if (active) { setBand(data.band); setError(''); }
      } catch (failure) {
        if (active) { setError(failure.message); setBand(null); }
      }
    };
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      active = false; window.removeEventListener('focus', refresh); window.clearInterval(timer);
    };
  }, [bandId, generation, user?.role]);

  if (error) return <div role="alert"><p>{error}</p><Link to="/preferences/bands">Back to Bands</Link></div>;
  if (!band) return <p role="status">Loading band...</p>;
  return (
    <BandContext.Provider value={{ band, bandId, libraryApi, refreshBand }}>
      <header style={{ padding: '12px 20px', overflowWrap: 'anywhere' }}>
        <Link to="/preferences/bands">All Bands</Link>
        <h1 style={{ fontSize: '1.7rem', margin: '10px 0' }}>{band.name}</h1>
        <Link to={`/bands/${bandId}`}>Band Admin</Link>
      </header>
      <Outlet />
    </BandContext.Provider>
  );
}

export function BandRoute() {
  const { bandId } = useParams();
  return <BandProvider key={bandId} bandId={bandId} />;
}

export function BandManagerRoute() {
  const { band } = useBand();
  const location = useLocation();
  return band.canManage ? <Outlet /> : <Navigate to="/logout" replace state={{
    from: location, authError: 'Only band managers can edit this band.',
  }} />;
}

export function useBand() { return useContext(BandContext); }
export function useLibraryApi() { return useBand()?.libraryApi || api; }
